import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, Clock, MapPin, Users, Loader2, CheckCircle, XCircle, Inbox, AlertTriangle, Award, Banknote, Percent, Star, Copy, Share2 } from "lucide-react";

const STATUS_STYLE = {
  PENDING_COACH_APPROVAL: "bg-amber-100 text-amber-700",
  APPROVED: "bg-blue-100 text-blue-700",
  ACTIVE: "bg-green-100 text-green-700",
  DRAFT: "bg-slate-100 text-slate-700",
  CANCELLED: "bg-red-100 text-red-700",
};
const STATUS_LABEL = {
  PENDING_COACH_APPROVAL: "Awaiting Your Approval",
  APPROVED: "You Approved",
  ACTIVE: "Published",
  DRAFT: "Draft",
  CANCELLED: "Cancelled",
};

export default function CoachEventRequests() {
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [declineDialog, setDeclineDialog] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [declining, setDeclining] = useState(false);
  const navigate = useNavigate();

  const fetchRequests = async () => {
    try {
      const res = await api.get("/coach/event-requests");
      setPending(res.data);
    } catch { setPending([]); }
    // Also fetch all events where this coach is assigned (for history)
    try {
      const res = await api.get("/coach/my-events");
      setHistory(res.data);
    } catch { setHistory([]); }
    setLoading(false);
  };

  useEffect(() => { fetchRequests(); }, []);

  const handleApprove = async (eventId) => {
    setActionId(eventId);
    try {
      await api.post(`/coach/events/${eventId}/approve`);
      toast.success("Event approved! The venue owner can now publish it.");
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to approve"); }
    setActionId(null);
  };

  const handleDecline = async () => {
    if (!declineDialog) return;
    setDeclining(true);
    try {
      await api.post(`/coach/events/${declineDialog}/decline`, { reason: declineReason });
      toast.success("Event request declined");
      setDeclineDialog(null);
      setDeclineReason("");
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to decline"); }
    setDeclining(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="coach-event-requests">
      <div className="flex items-center gap-3 mb-2">
        <Inbox className="w-7 h-7 text-court-orange" />
        <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">Event Requests</h1>
      </div>
      <p className="text-muted-foreground mb-8">Venue owners want you for their events. Review and respond below.</p>

      {/* Pending Requests */}
      <h2 className="font-heading text-lg uppercase tracking-tight font-bold mb-4 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-500" />
        Pending Requests ({pending.length})
      </h2>

      {pending.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 mb-10" data-testid="no-pending">
          <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
          <p className="font-heading text-muted-foreground uppercase">All caught up!</p>
          <p className="text-sm text-muted-foreground mt-1">No pending event requests at the moment.</p>
        </div>
      ) : (
        <div className="space-y-4 mb-10">
          {pending.map(e => (
            <div key={e.event_id} className="bg-white border-2 border-amber-200 p-5 hover:border-amber-300 transition-colors" data-testid={`pending-event-${e.event_id}`}>
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-base font-bold uppercase tracking-tight">{e.title}</h3>
                    <Badge className="bg-amber-100 text-amber-700 text-[10px] font-heading uppercase">Awaiting Your Approval</Badge>
                    {e.event_type && <Badge variant="outline" className="text-[10px] font-heading uppercase">{e.event_type}</Badge>}
                  </div>
                  {e.description && <p className="text-sm text-muted-foreground line-clamp-2">{e.description}</p>}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                    <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5 text-court-orange" />{e.event_date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{e.start_time} - {e.end_time}</span>
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />Capacity: {e.capacity}</span>
                    {e.venue_name && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{e.venue_name}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Requested by: <strong className="text-foreground">{e.organizer_name}</strong>
                    {e.space_name && <> at <strong className="text-foreground">{e.space_name}</strong></>}
                  </p>
                  {e.registration_type === "paid" && e.price > 0 && (
                    <p className="text-xs font-heading text-court-orange">Registration: ₱{e.price} per participant</p>
                  )}
                  {e.registration_type === "free" && (
                    <p className="text-xs font-heading text-green-600">Free Event</p>
                  )}
                  {/* Coach Offer Details */}
                  {e.coach_offer && (e.coach_offer.rate_per_hour || e.coach_offer.revenue_pct || e.coach_offer.rating_bonus) && (
                    <div className="bg-orange-50/60 border border-orange-200 p-3 mt-2 space-y-1" data-testid={`offer-details-${e.event_id}`}>
                      <p className="font-heading text-[10px] uppercase tracking-[0.15em] text-court-orange mb-1">Compensation Offer</p>
                      {e.coach_offer.rate_per_hour && (
                        <p className="flex items-center gap-1.5 text-xs">
                          <Banknote className="w-3.5 h-3.5 text-green-600" />
                          <span className="font-medium">₱{e.coach_offer.rate_per_hour.toLocaleString()}/hr</span>
                          <span className="text-muted-foreground">fixed hourly rate</span>
                        </p>
                      )}
                      {e.coach_offer.revenue_pct && (
                        <p className="flex items-center gap-1.5 text-xs">
                          <Percent className="w-3.5 h-3.5 text-purple-600" />
                          <span className="font-medium">{e.coach_offer.revenue_pct}%</span>
                          <span className="text-muted-foreground">of total event revenue</span>
                        </p>
                      )}
                      {e.coach_offer.rating_bonus && (
                        <p className="flex items-center gap-1.5 text-xs">
                          <Star className="w-3.5 h-3.5 text-amber-500" />
                          <span className="font-medium">₱{e.coach_offer.rating_bonus.toLocaleString()}</span>
                          <span className="text-muted-foreground">bonus for {e.coach_offer.rating_threshold || 4.0}★+ rating</span>
                        </p>
                      )}
                      {e.coach_offer.notes && (
                        <p className="text-[10px] text-muted-foreground border-t border-orange-100 pt-1 mt-1 italic">"{e.coach_offer.notes}"</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button data-testid={`approve-btn-${e.event_id}`} size="sm" onClick={() => handleApprove(e.event_id)}
                    disabled={actionId === e.event_id}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-full font-heading uppercase text-xs px-5">
                    {actionId === e.event_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                    Approve
                  </Button>
                  <Button data-testid={`decline-btn-${e.event_id}`} size="sm" variant="outline"
                    onClick={() => setDeclineDialog(e.event_id)}
                    className="rounded-full text-xs border-2 text-red-500 border-red-200 hover:bg-red-50 font-heading uppercase">
                    <XCircle className="w-3 h-3 mr-1" />Decline
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <>
          <h2 className="font-heading text-lg uppercase tracking-tight font-bold mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-court-orange" />
            My Events ({history.length})
          </h2>
          <div className="space-y-2">
            {history.map(e => (
              <div key={e.event_id}
                className="bg-white border-2 border-slate-100 p-4 hover:border-slate-200 transition-colors"
                data-testid={`history-event-${e.event_id}`}>
                <div className="flex items-center gap-3 flex-wrap cursor-pointer" onClick={() => e.status === "ACTIVE" ? navigate(`/events/${e.event_id}`) : null}>
                  <h3 className="font-heading text-sm font-bold uppercase tracking-tight">{e.title}</h3>
                  <Badge className={`${STATUS_STYLE[e.status] || 'bg-slate-100 text-slate-600'} text-[10px] font-heading uppercase`}>
                    {STATUS_LABEL[e.status] || e.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1 flex-wrap">
                  <span>{e.event_date}</span>
                  <span>{e.start_time} - {e.end_time}</span>
                  {e.venue_name && <span>{e.venue_name}</span>}
                  {e.coach_approval_status === "declined" && (
                    <span className="text-red-500">You declined this request</span>
                  )}
                </div>
                {e.status === "ACTIVE" && (
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
                    <Share2 className="w-3 h-3 text-green-600" />
                    <span className="text-[10px] text-muted-foreground font-mono truncate flex-1">{window.location.origin}/events/{e.event_id}/register</span>
                    <Button size="sm" variant="outline" className="h-6 rounded-full text-[10px] px-2 border"
                      data-testid={`share-btn-${e.event_id}`}
                      onClick={(ev) => { ev.stopPropagation(); navigator.clipboard.writeText(`${window.location.origin}/events/${e.event_id}/register`); toast.success("Link copied!"); }}>
                      <Copy className="w-2.5 h-2.5 mr-0.5" />Copy
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Decline Dialog */}
      <Dialog open={!!declineDialog} onOpenChange={v => { if (!v) { setDeclineDialog(null); setDeclineReason(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="decline-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Decline Event Request</DialogTitle>
            <DialogDescription>Let the venue owner know why you're unavailable (optional).</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="decline-reason" value={declineReason} onChange={e => setDeclineReason(e.target.value)}
            placeholder="e.g., I have a schedule conflict on that date..." className="border-2 rounded-none" rows={3} />
          <div className="flex gap-3 mt-2">
            <Button variant="outline" onClick={() => { setDeclineDialog(null); setDeclineReason(""); }}
              className="flex-1 rounded-full border-2">Cancel</Button>
            <Button data-testid="confirm-decline-btn" onClick={handleDecline} disabled={declining}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full font-heading uppercase">
              {declining ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
              Decline
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
